/**
 * Zoom Detection and Responsive Utilities
 * Handles cross-platform browser zoom detection and responsive breakpoints
 */

// TypeScript declarations for Visual Viewport API
declare global {
    interface Window {
        visualViewport?: {
            width: number;
            height: number;
            scale: number;
            addEventListener: (type: string, listener: () => void) => void;
            removeEventListener: (type: string, listener: () => void) => void;
        };
    }
}

export interface ZoomInfo {
    zoomLevel: number;
    devicePixelRatio: number;
    isZoomed: boolean;
    effectiveWidth: number;
    effectiveHeight: number;
}

/**
 * Detects browser zoom level and provides zoom-aware responsive utilities
 */
export class ZoomDetection {
    private static instance: ZoomDetection;
    private zoomInfo: ZoomInfo;
    private listeners: Array<(zoomInfo: ZoomInfo) => void> = [];

    private constructor() {
        this.zoomInfo = this.calculateZoomInfo();
        this.setupEventListeners();
    }

    public static getInstance(): ZoomDetection {
        if (!ZoomDetection.instance) {
            ZoomDetection.instance = new ZoomDetection();
        }
        return ZoomDetection.instance;
    }

    /**
     * Calculate zoom information based on multiple detection methods
     */
    private calculateZoomInfo(): ZoomInfo {
        const devicePixelRatio = window.devicePixelRatio || 1;
        const screenWidth = window.screen.width;
        const screenHeight = window.screen.height;
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;

        // Method 1: Compare screen dimensions with window dimensions
        const zoomByScreen = Math.round((screenWidth / windowWidth) * 100) / 100;

        // Method 2: Use devicePixelRatio (more reliable on high-DPI displays)
        const zoomByDPR = Math.round(devicePixelRatio * 100) / 100;

        // Method 3: Use outerWidth vs innerWidth (fallback)
        const zoomByOuter = Math.round((window.outerWidth / windowWidth) * 100) / 100;

        // Method 4: Use Visual Viewport API if available (most accurate for iOS)
        let zoomByVisualViewport = 1;
        if (window.visualViewport && window.visualViewport.scale) {
            zoomByVisualViewport = Math.round(window.visualViewport.scale * 100) / 100;
        }

        // Use the most reliable method based on platform
        let zoomLevel: number;
        if (this.isIOS()) {
            // iOS: Use Visual Viewport API if available, otherwise assume no zoom
            // iOS Safari reports zoom differently, so we default to 1.0 unless Visual Viewport shows otherwise
            if (window.visualViewport && window.visualViewport.scale) {
                zoomLevel = zoomByVisualViewport;
            } else {
                // On iOS, devicePixelRatio is typically 2 or 3, not zoom level
                // So we assume no zoom unless we can detect it via Visual Viewport
                zoomLevel = 1.0;
            }
        } else if (this.isWindows()) {
            // Windows: Use screen-based detection as primary method
            zoomLevel = zoomByScreen;
        } else if (this.isMacOS()) {
            // macOS: Use devicePixelRatio as primary method
            zoomLevel = zoomByDPR;
        } else {
            // Fallback: Use outerWidth method
            zoomLevel = zoomByOuter;
        }

        // Clamp zoom level to reasonable bounds
        zoomLevel = Math.max(0.25, Math.min(5.0, zoomLevel));

        const isZoomed = Math.abs(zoomLevel - 1.0) > 0.05; // 5% tolerance

        return {
            zoomLevel,
            devicePixelRatio,
            isZoomed,
            effectiveWidth: windowWidth,
            effectiveHeight: windowHeight
        };
    }

    /**
     * Get current zoom information
     */
    public getZoomInfo(): ZoomInfo {
        return { ...this.zoomInfo };
    }

    /**
     * Get zoom-aware breakpoint detection
     */
    public getResponsiveBreakpoint(): 'mobile' | 'tablet' | 'desktop' {
        const { effectiveWidth, zoomLevel } = this.zoomInfo;

        // Adjust breakpoints based on zoom level to maintain consistent behavior
        let mobileBreakpoint = 768;
        let tabletBreakpoint = 992;

        // Compensate for zoom by adjusting breakpoints
        if (zoomLevel < 1.0) {
            // Zoomed out: increase breakpoints
            mobileBreakpoint = Math.round(mobileBreakpoint / zoomLevel);
            tabletBreakpoint = Math.round(tabletBreakpoint / zoomLevel);
        } else if (zoomLevel > 1.0) {
            // Zoomed in: decrease breakpoints
            mobileBreakpoint = Math.round(mobileBreakpoint / zoomLevel);
            tabletBreakpoint = Math.round(tabletBreakpoint / zoomLevel);
        }

        if (effectiveWidth < mobileBreakpoint) {
            return 'mobile';
        } else if (effectiveWidth < tabletBreakpoint) {
            return 'tablet';
        } else {
            return 'desktop';
        }
    }

    /**
     * Check if current viewport is mobile
     */
    public isMobile(): boolean {
        return this.getResponsiveBreakpoint() === 'mobile';
    }

    /**
     * Check if current viewport is tablet
     */
    public isTablet(): boolean {
        return this.getResponsiveBreakpoint() === 'tablet';
    }

    /**
     * Check if current viewport is desktop
     */
    public isDesktop(): boolean {
        return this.getResponsiveBreakpoint() === 'desktop';
    }

    /**
     * Subscribe to zoom changes
     */
    public onZoomChange(callback: (zoomInfo: ZoomInfo) => void): () => void {
        this.listeners.push(callback);

        // Return unsubscribe function
        return () => {
            const index = this.listeners.indexOf(callback);
            if (index > -1) {
                this.listeners.splice(index, 1);
            }
        };
    }

    /**
     * Update zoom information and notify listeners
     */
    private updateZoomInfo(): void {
        this.zoomInfo = this.calculateZoomInfo();
        this.listeners.forEach(callback => callback(this.zoomInfo));
    }

    /**
     * Setup event listeners for zoom and resize changes
     */
    private setupEventListeners(): void {
        // Listen for resize events
        window.addEventListener('resize', () => {
            this.updateZoomInfo();
        });

        // Listen for orientation changes (mobile)
        window.addEventListener('orientationchange', () => {
            // Delay to allow orientation change to complete
            setTimeout(() => {
                this.updateZoomInfo();
            }, 100);
        });

        // Listen for zoom events (if supported)
        if ('visualViewport' in window) {
            window.visualViewport?.addEventListener('resize', () => {
                this.updateZoomInfo();
            });
        }
    }

    /**
     * Platform detection utilities
     */
    private isWindows(): boolean {
        return navigator.platform.toLowerCase().includes('win');
    }

    private isMacOS(): boolean {
        return navigator.platform.toLowerCase().includes('mac');
    }

    private isIOS(): boolean {
        return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
            (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    }

    /**
     * Get CSS custom properties for zoom-aware styling
     */
    public getCSSProperties(): Record<string, string> {
        const { zoomLevel, isZoomed } = this.zoomInfo;

        return {
            '--zoom-level': zoomLevel.toString(),
            '--is-zoomed': isZoomed ? '1' : '0',
            '--zoom-compensation': (1 / zoomLevel).toString()
        };
    }

    /**
     * Apply zoom-aware CSS properties to document root
     */
    public applyCSSProperties(): void {
        const properties = this.getCSSProperties();
        const root = document.documentElement;

        Object.entries(properties).forEach(([property, value]) => {
            root.style.setProperty(property, value);
        });
    }
}

// Export singleton instance
export const zoomDetection = ZoomDetection.getInstance();

// Export utility functions
export const getZoomInfo = () => zoomDetection.getZoomInfo();
export const getResponsiveBreakpoint = () => zoomDetection.getResponsiveBreakpoint();
export const isMobile = () => zoomDetection.isMobile();
export const isTablet = () => zoomDetection.isTablet();
export const isDesktop = () => zoomDetection.isDesktop();
export const onZoomChange = (callback: (zoomInfo: ZoomInfo) => void) => zoomDetection.onZoomChange(callback);
